// src/app/components/chat/ChatSharedCardBubble.tsx
//
// Preview de um BoardCard compartilhado no chat interno (ver CLAUDE.md,
// seção "chat-card-share"). O preview (título, board, setores) é sempre
// visível a qualquer participante do grupo — o gate de acesso real só
// entra em jogo quando o usuário clica para abrir o detalhe completo:
// `chatService.getSharedCardDetail` chama um endpoint dedicado
// (GET /chat/messages/{id}/card/) que devolve 403 quando o usuário não
// tem acesso ao setor do card (considerando também cards espelhados —
// basta acesso a QUALQUER board do mirror_group). Nesse caso o dialog
// abre normalmente, só que com uma mensagem de "sem acesso" em vez do
// conteúdo — nunca falha silenciosamente nem esconde que o card existe.
//
// O corpo do dialog (descrição, checklist, comentários, anexos) vive em
// CardPeekDialog.tsx — extraído daqui pra ser reaproveitado também pelo
// peek de card na Central de Atendimento (ver TimelineEntryRow, shared.tsx).

import { useState } from 'react';
import { Badge } from '../ui/badge';
import { chatService, type ChatCardShare } from '@/app/services/chatService';
import type { CardDetailData } from '@/app/pages/board-completo/card-detail/types';
import { CardPeekDialog } from '../cards/CardPeekDialog';
import { Layout, Lock } from 'lucide-react';

export interface ChatSharedCardBubbleProps {
  messageId: string;
  share: ChatCardShare;
  /** Legenda opcional digitada no envio (ShareCardToChatModal) — renderizada
   * dentro do mesmo container do card, não como uma mensagem de texto solta,
   * pra deixar claro que é uma legenda DESTE card. */
  caption?: string;
}

export function ChatSharedCardBubble({ messageId, share, caption }: ChatSharedCardBubbleProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="mt-1.5 max-w-sm w-full rounded-lg border border-slate-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] overflow-hidden">
        {caption && (
          <p className="px-3 pt-2.5 pb-1.5 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words border-b border-slate-100 dark:border-white/[0.06]">
            {caption}
          </p>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-start gap-2.5 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-white/[0.06] text-left w-full transition-colors"
        >
          <div className="mt-0.5 flex-shrink-0 size-8 rounded-md bg-indigo-50 dark:bg-indigo-500/15 flex items-center justify-center">
            <Layout className="size-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{share.title}</p>
              {!share.can_view && <Lock className="size-3 flex-shrink-0 text-slate-400 dark:text-slate-500" />}
            </div>
            {share.board_name && (
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{share.board_name}</p>
            )}
            {share.sector_names.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {share.sector_names.map((name) => (
                  <Badge key={name} variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                    {name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </button>
      </div>

      <CardPeekDialog
        open={open}
        onOpenChange={setOpen}
        title={share.title}
        isAvailable={share.is_available}
        forbiddenMessage={
          share.sector_names.length > 0
            ? `É necessário acesso ao setor ${share.sector_names.join(' ou ')} para ver os detalhes.`
            : undefined
        }
        fetchDetail={() => chatService.getSharedCardDetail(messageId) as Promise<CardDetailData>}
      />
    </>
  );
}